import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useStore } from "../../../stores/store";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useSubmitData } from "../../../hooks/useSubmitData";
import { useSetMenu } from "../../../hooks/useSetMenu";
import FormLayout from '../../Generic/FormLayout';
import FormInput from '../../Generic/FormInput';
import * as yup from "yup";
import FormSelect from '../../Generic/FormSelect';
import FormCheckbox from '../../Generic/FormCheckbox';

const Add = () => {
    const { mainStore } = useStore();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    useSetMenu(mainStore.CriteriaMenuType, mainStore.CriteriaMenuNo);
    
    // Add state for the integrated CriteriaValue creation
    const [createdCriteriaId, setCreatedCriteriaId] = useState(null);
    const [showValueForm, setShowValueForm] = useState(false);
    const [batchValues, setBatchValues] = useState("");
    const [valuesArray, setValuesArray] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const onError = (errors, e) =>
        enqueueSnackbar(t("onErrorSubmit"), { variant: "error" });

    const schema = yup.object({
        name: yup
            .string()
            .required(t('requiredField'))
            .max(100, t('maxLength', { max: 100 })),
        subcategoryId: yup.string().required(t("selectRequired")),
        panelId: yup.string().required(t("selectRequired")),
        categoryId: yup.string().required(t("selectRequired")),
        weightCoefficient: yup.string().required(t("selectRequired")),
    });

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
    });

    // Modified to show the criteria value form after successful creation
    const onSubmit = async (data) => {
        try {
            setIsSubmitting(true);
            const response = await axiosPrivate.post("/Criterias", data);
            
            if (response && response.data && response.data.id) {
                enqueueSnackbar(t("criteriaCreateSuccess"), { variant: "success" });
                setCreatedCriteriaId(response.data.id);
                setShowValueForm(true);
            } else {
                enqueueSnackbar(t("criteriaCreateError"), { variant: "error" });
                navigate("/Criteria/List");
            }
        } catch (error) {
            enqueueSnackbar(t("criteriaCreateError"), { variant: "error" });
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // Function to handle batch values submission
    const handleValuesSubmit = async () => {
        if (valuesArray.length === 0) {
            enqueueSnackbar(t("pleaseAddAtLeastOneValue"), { variant: "warning" });
            return;
        }
        
        try {
            setIsSubmitting(true);
            
            const batchRequest = {
                criteriaId: createdCriteriaId,
                panelId: parseInt(getValues("panelId"), 10),
                categoryId: parseInt(getValues("categoryId"), 10),
                subcategoryId: parseInt(getValues("subcategoryId"), 10),
                values: valuesArray
            };
            
            await axiosPrivate.post("/CriteriaValues/BatchCreate", batchRequest);
            enqueueSnackbar(t("valuesAddedSuccess"), { variant: "success" });
            navigate("/Criteria/List");
        } catch (error) {
            enqueueSnackbar(t("valuesAddedError"), { variant: "error" });
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // Function to skip adding values and go directly to the list
    const skipValuesAndGoToList = () => {
        navigate("/Criteria/List");
    };
    
    const onCancel = () => navigate(-1);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line
    }, [t]);
    
    // Process the batch input into an array of values
    useEffect(() => {
        if (batchValues.trim()) {
            // Split by newlines, commas, or semicolons
            const values = batchValues
                .split(/[\n,;]/)
                .map(val => val.trim())
                .filter(val => val.length > 0 && val.length <= 250);
            
            setValuesArray(values);
        } else {
            setValuesArray([]);
        }
    }, [batchValues]);

    //#region Subcategory Select
    const [panels, setPanels] = useState([]);
    const [selectedPanel, setSelectedPanel] = useState(null);

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);


    const [weightCoefficients, setWeightCoefficients] = useState([
        { value: 1.0, label: '1.0' },
        { value: 0.5, label: '0.5' },
        { value: 0.33, label: '0.33' }
    ]);
    async function loadData() {
        try {
            const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
            setPanels(panelsResponse.data);
        } catch (err) {
            enqueueSnackbar(t("loadDataError"), { variant: "error" });
        }
    }
    const handleSubcategoryChange = selectedOption => {
        setValue("subcategoryId", selectedOption.value, { shouldValidate: true });
        setSelectedSubcategory(selectedOption);
    };
    const handleCategoryChange = async (selectedOption) => {
        setValue("categoryId", selectedOption.value, { shouldValidate: true });
        setSelectedCategory(selectedOption);
        try {
            const response = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setSubcategories([]);
            } else {
                setSubcategories(response.data);
            }
            setSelectedSubcategory(null);
            setValue("subcategoryId", null, { shouldValidate: true });

        } catch (error) {
            enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
        }
    };

    const handlePanelChange = async (selectedOption) => {
        setValue("panelId", selectedOption.value, { shouldValidate: true });
        setSelectedPanel(selectedOption);
        try {
            const response = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setCategories([]);
            } else {
                setCategories(response.data);
            }
            setSubcategories([]);

            setSelectedSubcategory(null);
            setSelectedCategory(null);

            setValue("categoryId", null, { shouldValidate: true });
            setValue("subcategoryId", null, { shouldValidate: true });

        } catch (error) {
            enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
        }
    };
    const handleWeightCoefficientChange = selectedOption => {
        setValue("weightCoefficient", selectedOption.value, { shouldValidate: true });
    };
    //#endregion

    return (
        <div>
            {!showValueForm ? (
                // Step 1: Create the Criteria
                <FormLayout title={t("criteriaCreateTitle")} onSubmit={handleSubmit(onSubmit, onError)} onCancel={onCancel}>
                    <FormSelect label="panel" name="panelId" register={register} errors={errors} colSize={4} options={panels} onChange={handlePanelChange} />
                    <FormSelect label="category" name="categoryId" value={selectedCategory} register={register} errors={errors} colSize={4} options={categories} onChange={handleCategoryChange} />
                    <FormSelect label="subcategory" name="subcategoryId" value={selectedSubcategory} register={register} errors={errors} colSize={4} options={subcategories} onChange={handleSubcategoryChange} />
                    <FormInput label="criteria" name="name" register={register} errors={errors} />
                    <FormSelect label="criteriaWeightCoefficient" name="weightCoefficient" register={register} errors={errors} options={weightCoefficients} onChange={handleWeightCoefficientChange} />
                    <FormCheckbox label="isMandatory" register={register} name="isMandatory" colSize={4} />
                </FormLayout>
            ) : (
                // Step 2: Add CriteriaValues to the created Criteria
                <div className="card p-4 mb-4">
                    <h2 className="mb-3">{t("addValuesTitle")}</h2>
                    <p className="text-success mb-4">{t("criteriaCreatedNowAddValues")}</p>
                    
                    <div className="mb-3">
                        <label className="form-label">{t("batchValues")}</label>
                        <textarea
                            className="form-control"
                            rows={5}
                            placeholder={t("enterMultipleValues")}
                            value={batchValues}
                            onChange={(e) => setBatchValues(e.target.value)}
                        />
                        <div className="form-text">
                            {t("batchValuesHelp")}
                        </div>
                    </div>
                    
                    {/* Preview of parsed values */}
                    {valuesArray.length > 0 && (
                        <div className="mt-3 mb-4">
                            <h5>{t("valuePreview")} ({valuesArray.length})</h5>
                            <div className="border p-2 bg-light">
                                {valuesArray.map((val, index) => (
                                    <span key={index} className="badge bg-secondary me-1 mb-1">{val}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    <div className="d-flex justify-content-between mt-3">
                        <button 
                            type="button" 
                            className="btn btn-secondary"
                            onClick={skipValuesAndGoToList}
                        >
                            {t("skipAndGoToList")}
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-primary"
                            onClick={handleValuesSubmit}
                            disabled={isSubmitting || valuesArray.length === 0}
                        >
                            {isSubmitting ? t("submitting") : t("saveValuesAndGoToList")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Add;